<script setup>
import { ref, computed } from 'vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PrestaShopAPI from '../../../api/integrations/prestashop';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['contact-synced']);

const loadingDetails = ref(false);
const details = ref(null);
const detailError = ref('');
const showDetails = ref(false);
const syncing = ref(false);
const syncResult = ref(null);

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCurrency = (amount, currency) => {
  if (!amount) return '-';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(amount);
};

const stateName = computed(() => {
  return props.order.state_name || `Estado ${props.order.current_state}`;
});

const toggleDetails = async () => {
  if (details.value) {
    showDetails.value = !showDetails.value;
    return;
  }
  loadingDetails.value = true;
  showDetails.value = true;
  detailError.value = '';
  try {
    const { data } = await PrestaShopAPI.getOrderDetails(
      props.order.hook_id,
      props.order.id
    );
    details.value = data.order;
  } catch (e) {
    detailError.value = e.response?.data?.error || 'Error al cargar detalles';
  } finally {
    loadingDetails.value = false;
  }
};

const syncContact = async () => {
  if (!props.contactId || !props.order.hook_id) return;
  syncing.value = true;
  syncResult.value = null;
  try {
    const { data } = await PrestaShopAPI.syncContact(
      props.order.hook_id,
      props.order.id,
      props.contactId
    );
    syncResult.value = data;
    if (data.updated && Object.keys(data.updated).length > 0 || data.merged?.length > 0) {
      emit('contact-synced', data);
    }
  } catch (e) {
    syncResult.value = { message: e.response?.data?.error || 'Error al sincronizar' };
  } finally {
    syncing.value = false;
  }
};

const formatAddress = addr => {
  if (!addr) return '';
  const parts = [
    [addr.firstname, addr.lastname].filter(Boolean).join(' '),
    addr.company,
    addr.address1,
    addr.address2,
    [addr.postcode, addr.city].filter(Boolean).join(' '),
    addr.country,
  ].filter(Boolean);
  return parts.join(', ');
};
</script>

<template>
  <div
    class="py-3 border-b border-n-weak last:border-b-0 flex flex-col gap-1.5"
  >
    <!-- Order header -->
    <div class="flex justify-between items-center">
      <div class="font-medium flex items-center gap-1.5">
        <span class="text-n-slate-12 text-sm">
          {{ order.reference || `#${order.id}` }}
        </span>
        <span
          v-if="order.store_name"
          class="text-xs px-1.5 py-0.5 rounded bg-n-alpha-5 text-n-slate-10"
        >
          {{ order.store_name }}
        </span>
      </div>
      <span
        class="text-xs px-2 py-1 rounded capitalize truncate bg-n-solid-3 text-n-slate-12"
      >
        {{ stateName }}
      </span>
    </div>
    <div class="text-sm text-n-slate-12 flex items-center gap-2">
      <span class="text-n-slate-11 border-r border-n-weak pr-2">
        {{ formatDate(order.date_add) }}
      </span>
      <span class="text-n-slate-11">
        {{ formatCurrency(order.total_paid, order.currency) }}
      </span>
    </div>

    <!-- Action buttons -->
    <div class="mt-1 flex flex-wrap items-center gap-1">
      <button
        v-if="!loadingDetails"
        class="text-xs px-2 py-0.5 rounded text-n-blue-11 hover:bg-n-blue-2 transition-colors"
        @click="toggleDetails"
      >
        {{ showDetails && details ? 'Ocultar' : 'Detalles' }}
      </button>
      <span class="text-n-weak">|</span>
      <button
        v-if="contactId && !syncing"
        class="text-xs px-2 py-0.5 rounded text-n-slate-11 hover:bg-n-alpha-5 transition-colors"
        @click="syncContact"
      >
        Sync contacto
      </button>
      <span v-if="order.bo_url" class="text-n-weak">|</span>
      <a
        v-if="order.bo_url"
        :href="order.bo_url"
        target="_blank"
        rel="noopener noreferrer"
        class="text-xs px-2 py-0.5 rounded text-n-slate-11 hover:bg-n-alpha-5 transition-colors no-underline"
      >
        BO &#8599;
      </a>
    </div>

    <!-- Sync result -->
    <div
      v-if="syncing"
      class="text-xs text-n-slate-10 py-0.5"
    >
      Sincronizando...
    </div>
    <div
      v-if="syncResult"
      class="text-xs py-0.5"
      :class="syncResult.merged?.length > 0
        ? 'text-n-blue-11'
        : syncResult.updated && Object.keys(syncResult.updated).length > 0
          ? 'text-n-teal-11'
          : 'text-n-slate-10'"
    >
      {{ syncResult.message }}
    </div>

    <!-- Loading -->
    <div v-if="loadingDetails" class="flex justify-center py-2">
      <Spinner size="20" class="text-n-brand" />
    </div>

    <!-- Error -->
    <div v-if="detailError" class="text-xs text-n-ruby-11 py-1">
      {{ detailError }}
    </div>

    <!-- Details panel -->
    <div v-if="details && showDetails" class="mt-2 pl-2 border-l-2 border-n-weak flex flex-col gap-3 text-xs">
      <!-- General info -->
      <div class="flex flex-col gap-1">
        <div class="grid grid-cols-2 gap-1">
          <div>
            <span class="text-n-slate-10">Pago:</span>
            <span class="ml-1 text-n-slate-12">{{ details.payment || '-' }}</span>
          </div>
          <div>
            <span class="text-n-slate-10">Envio:</span>
            <span class="ml-1 text-n-slate-12">
              {{ formatCurrency(details.total_shipping, details.currency) }}
            </span>
          </div>
          <div v-if="details.carrier_name">
            <span class="text-n-slate-10">Transportista:</span>
            <span class="ml-1 text-n-slate-12">{{ details.carrier_name }}</span>
          </div>
        </div>
        <div v-if="details.shipping_number">
          <span class="text-n-slate-10">Tracking:</span>
          <span class="ml-1 text-n-slate-12 break-all select-all">{{ details.shipping_number }}</span>
        </div>
      </div>

      <!-- Status history -->
      <div v-if="details.history && details.history.length > 0">
        <p class="text-n-slate-10 font-medium mb-1">Historial de estados:</p>
        <div class="flex flex-col gap-0.5">
          <div
            v-for="(entry, idx) in details.history"
            :key="entry.id || idx"
            class="flex items-center gap-2 py-0.5"
          >
            <span class="text-n-slate-10 whitespace-nowrap">
              {{ formatDateTime(entry.date_add) }}
            </span>
            <span
              class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
              :class="idx === 0
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-alpha-5 text-n-slate-11'"
            >
              {{ entry.state_name }}
            </span>
          </div>
        </div>
      </div>

      <!-- Items -->
      <div v-if="details.items && details.items.length > 0">
        <p class="text-n-slate-10 font-medium mb-1">
          Articulos ({{ details.items.length }}):
        </p>
        <div
          v-for="item in details.items"
          :key="item.id"
          class="flex justify-between py-1 border-b border-n-weak last:border-b-0"
        >
          <span class="text-n-slate-12 flex-1 truncate pr-2">
            {{ item.product_name }}
            <span class="text-n-slate-10">x{{ item.product_quantity }}</span>
          </span>
          <span class="text-n-slate-11 whitespace-nowrap">
            {{ formatCurrency(item.total_price_tax_incl, details.currency) }}
          </span>
        </div>
      </div>

      <!-- Shipping address -->
      <div v-if="details.shipping_address">
        <p class="text-n-slate-10 font-medium mb-0.5">Direccion de envio:</p>
        <p class="text-n-slate-11">
          {{ formatAddress(details.shipping_address) }}
        </p>
        <p
          v-if="details.shipping_address.phone || details.shipping_address.phone_mobile"
          class="text-n-slate-10 mt-0.5"
        >
          Tel: {{ details.shipping_address.phone_mobile || details.shipping_address.phone }}
        </p>
      </div>

      <!-- Billing address -->
      <div v-if="details.billing_address">
        <p class="text-n-slate-10 font-medium mb-0.5">Direccion de facturacion:</p>
        <p class="text-n-slate-11">
          {{ formatAddress(details.billing_address) }}
        </p>
      </div>
    </div>
  </div>
</template>
